import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  deleteCourierConfigurationApi,
  getCourierConfigurationApi,
  getTenantCourierConfigurationApi,
  postCourierConfigurationApi,
  updateCourierConfigurationApi,
} from "../../api/common/CourierConfigurationApi";

const initialState = {
  courierConfiguration: null,
  tenantCourierConfiguration: null,
  loading: false,
  saving: false,
  deleting: false,
  error: null,
};

const createConfigurationThunk = (name, api) =>
  createAsyncThunk(`courierConfiguration/${name}`, async (payload, { rejectWithValue }) => {
    try {
      const response = await api(payload);
      return response.data;
    } catch (err) {
      // A tenant without saved settings can create its first configuration.
      if (name === "getTenantCourierConfiguration" && err?.response?.status === 404) {
        return { response: null };
      }
      return rejectWithValue({
        errorMessage: err?.response?.data?.message || err?.message || "Something went wrong",
      });
    }
  });

export const getCourierConfiguration = createConfigurationThunk(
  "getCourierConfiguration",
  getCourierConfigurationApi,
);
export const getTenantCourierConfiguration = createConfigurationThunk(
  "getTenantCourierConfiguration",
  getTenantCourierConfigurationApi,
);
export const postCourierConfiguration = createConfigurationThunk(
  "postCourierConfiguration",
  postCourierConfigurationApi,
);
export const updateCourierConfiguration = createConfigurationThunk(
  "updateCourierConfiguration",
  updateCourierConfigurationApi,
);
export const deleteCourierConfiguration = createConfigurationThunk(
  "deleteCourierConfiguration",
  deleteCourierConfigurationApi,
);

const courierConfigurationSlice = createSlice({
  name: "courierConfiguration",
  initialState,
  reducers: {
    clearCourierConfigurationError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    const operations = [
      [getCourierConfiguration, "loading", "courierConfiguration"],
      [getTenantCourierConfiguration, "loading", "tenantCourierConfiguration"],
      [postCourierConfiguration, "saving", "courierConfiguration"],
      [updateCourierConfiguration, "saving", "courierConfiguration"],
      [deleteCourierConfiguration, "deleting", null],
    ];

    operations.forEach(([thunk, statusKey, dataKey]) => {
      builder
        .addCase(thunk.pending, (state) => {
          state[statusKey] = true;
          state.error = null;
        })
        .addCase(thunk.fulfilled, (state, action) => {
          state[statusKey] = false;
          if (dataKey) {
            state[dataKey] = action.payload?.response ?? null;
          } else {
            const deletedId = action.meta.arg;
            if (state.courierConfiguration?.id === deletedId) {
              state.courierConfiguration = null;
            }
            if (state.tenantCourierConfiguration?.id === deletedId) {
              state.tenantCourierConfiguration = null;
            }
          }
        })
        .addCase(thunk.rejected, (state, action) => {
          state[statusKey] = false;
          state.error = action.payload?.errorMessage || action.error?.message || "Something went wrong";
        });
    });
  },
});

export const { clearCourierConfigurationError } = courierConfigurationSlice.actions;
export default courierConfigurationSlice.reducer;
